import { useEffect } from 'react';

/**
 * The notice for rows the source sent but the picker could not offer.
 *
 * @param droppedRowCount - How many rows of the page were left out.
 * @returns A sentence for the picker notice, in the singular for one row.
 */
export function describeDroppedRows(droppedRowCount: number): string {
  return droppedRowCount === 1
    ? '1 item from the source was incomplete and is not shown.'
    : `${droppedRowCount} items from the source were incomplete and are not shown.`;
}

/**
 * Logs a console warning once for each page that arrives with rows the picker
 * could not offer.
 *
 * @remarks
 * The warning carries counts only, never names or ids. It goes to
 * `console.warn`, which the browser console shows and the Datadog RUM version
 * in use does not collect (it listens to `console.error`), because a dropped row is a gap in the source and not our error.
 *
 * The hook warns again whenever `pageKey` changes while the count is positive.
 * A caller that shows the previous page under a new key, such as TanStack
 * Query's `keepPreviousData`, passes `undefined` for that placeholder page, or
 * the previous page's warning repeats.
 *
 * @param source - The picker name that prefixes the warning.
 * @param droppedRowCount - How many rows of the page were left out, or `undefined` before a page arrived or while the page is placeholder data.
 * @param pageKey - A value that changes with every page that arrives, such as TanStack Query's `dataUpdatedAt`.
 */
export function useDroppedRowsWarning(
  source: string,
  droppedRowCount: number | undefined,
  pageKey: number,
): void {
  useEffect(() => {
    if (droppedRowCount !== undefined && droppedRowCount > 0) {
      console.warn(
        `${source}: ${droppedRowCount} rows from the source were incomplete and are not offered.`,
      );
    }
  }, [source, droppedRowCount, pageKey]);
}
